import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './Translations.css'

const ALL_LANGUAGES = [
  { code: 'de', name: 'Deutsch',     flag: '🇩🇪' },
  { code: 'en', name: 'English',     flag: '🇬🇧' },
  { code: 'es', name: 'Español',     flag: '🇪🇸' },
  { code: 'it', name: 'Italiano',    flag: '🇮🇹' },
  { code: 'pl', name: 'Polski',      flag: '🇵🇱' },
  { code: 'nl', name: 'Nederlands',  flag: '🇳🇱' },
  { code: 'da', name: 'Dansk',       flag: '🇩🇰' },
  { code: 'fr', name: 'Français',    flag: '🇫🇷' },
  { code: 'hu', name: 'Magyar',      flag: '🇭🇺' },
  { code: 'hr', name: 'Hrvatski',    flag: '🇭🇷' },
  { code: 'pt', name: 'Português',   flag: '🇵🇹' },
  { code: 'cs', name: 'Čeština',     flag: '🇨🇿' },
  { code: 'bg', name: 'Български',   flag: '🇧🇬' },
  { code: 'et', name: 'Eesti',       flag: '🇪🇪' },
  { code: 'fi', name: 'Suomi',       flag: '🇫🇮' },
  { code: 'el', name: 'Ελληνικά',    flag: '🇬🇷' },
  { code: 'lv', name: 'Latviešu',    flag: '🇱🇻' },
  { code: 'lt', name: 'Lietuvių',    flag: '🇱🇹' },
  { code: 'no', name: 'Norsk',       flag: '🇳🇴' },
  { code: 'ro', name: 'Română',      flag: '🇷🇴' },
  { code: 'ru', name: 'Русский',     flag: '🇷🇺' },
  { code: 'sr', name: 'Српски',      flag: '🇷🇸' },
  { code: 'sk', name: 'Slovenčina',  flag: '🇸🇰' },
  { code: 'sl', name: 'Slovenščina', flag: '🇸🇮' },
  { code: 'sv', name: 'Svenska',     flag: '🇸🇪' },
  { code: 'tr', name: 'Türkçe',      flag: '🇹🇷' },
  { code: 'uk', name: 'Українська',  flag: '🇺🇦' },
]

// Keys that hold arrays (e.g. safety_rules, questions) — skip in the UI
const SKIP_KEYS = new Set(['safety_rules', 'questions'])

const Translations = () => {
  const { apiFetch, admin, isOwner } = useAuth()

  const [tenants,        setTenants]        = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [activeLang,     setActiveLang]     = useState('de')
  const [search,         setSearch]         = useState('')
  const [showOverrides,  setShowOverrides]  = useState(false)

  // defaults[lang][key] = default string
  // overrides[lang][key] = custom string
  const [defaults,  setDefaults]  = useState({})
  const [overrides, setOverrides] = useState({})

  // Local edits: { key: value } for the active language
  const [localEdits, setLocalEdits] = useState({})
  const [saving,     setSaving]     = useState({}) // { key: true/false }
  const [saveError,  setSaveError]  = useState({}) // { key: 'error message' }

  const [loading, setLoading] = useState(false)

  // Load tenants (owner only)
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants')
        .then(r => r.json())
        .then(data => {
          setTenants(data)
          if (data.length > 0) setSelectedTenant(data[0].id)
        })
    } else {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner])

  // Load translations when tenant changes
  const loadTranslations = () => {
    if (!selectedTenant) return
    setLoading(true)
    apiFetch(`/api/admin/ui-translations/${selectedTenant}`)
      .then(r => r.json())
      .then(data => {
        setDefaults(data.defaults || {})
        setOverrides(data.overrides || {})
        setLocalEdits({})
      })
      .finally(() => setLoading(false))
  }

  useEffect(loadTranslations, [selectedTenant])

  // Reset local edits when language tab changes
  useEffect(() => {
    setLocalEdits({})
    setSaveError({})
  }, [activeLang, selectedTenant])

  const getKeys = useCallback(() => {
    const langDefaults = defaults[activeLang] || {}
    return Object.keys(langDefaults)
      .filter(k => !SKIP_KEYS.has(k) && typeof langDefaults[k] === 'string')
      .filter(k => {
        if (showOverrides) return !!(overrides[activeLang]?.[k])
        return true
      })
      .filter(k => {
        if (!search) return true
        const q = search.toLowerCase()
        const defaultVal = langDefaults[k] || ''
        const overrideVal = overrides[activeLang]?.[k] || ''
        return k.toLowerCase().includes(q) ||
          defaultVal.toLowerCase().includes(q) ||
          overrideVal.toLowerCase().includes(q)
      })
  }, [defaults, overrides, activeLang, search, showOverrides])

  const overrideCount = Object.keys(overrides[activeLang] || {}).length

  const getValue = (key) => {
    if (key in localEdits) return localEdits[key]
    return overrides[activeLang]?.[key] ?? defaults[activeLang]?.[key] ?? ''
  }

  const isOverridden = (key) => !!(overrides[activeLang]?.[key])

  const handleChange = (key, value) => {
    setLocalEdits(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async (key) => {
    if (!(key in localEdits)) return
    const value = localEdits[key]
    const defaultVal = defaults[activeLang]?.[key] ?? ''

    // If the value matches the default, treat as a reset
    const saveValue = value === defaultVal ? null : value

    setSaving(prev => ({ ...prev, [key]: true }))
    setSaveError(prev => ({ ...prev, [key]: null }))
    try {
      const res = await apiFetch(`/api/admin/ui-translations/${selectedTenant}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang: activeLang, key, value: saveValue }),
      })
      if (!res.ok) throw new Error('Fehler beim Speichern')
      // Update local overrides state
      setOverrides(prev => {
        const langOverrides = { ...(prev[activeLang] || {}) }
        if (saveValue === null) {
          delete langOverrides[key]
        } else {
          langOverrides[key] = saveValue
        }
        return { ...prev, [activeLang]: langOverrides }
      })
      setLocalEdits(prev => { const n = { ...prev }; delete n[key]; return n })
    } catch {
      setSaveError(prev => ({ ...prev, [key]: 'Fehler' }))
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }))
    }
  }

  const handleReset = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }))
    setSaveError(prev => ({ ...prev, [key]: null }))
    try {
      const res = await apiFetch(`/api/admin/ui-translations/${selectedTenant}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang: activeLang, key, value: null }),
      })
      if (!res.ok) throw new Error('Fehler')
      setOverrides(prev => {
        const langOverrides = { ...(prev[activeLang] || {}) }
        delete langOverrides[key]
        return { ...prev, [activeLang]: langOverrides }
      })
      setLocalEdits(prev => { const n = { ...prev }; delete n[key]; return n })
    } catch {
      setSaveError(prev => ({ ...prev, [key]: 'Fehler' }))
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }))
    }
  }

  const handleTranslate = async (key) => {
    const value = key in localEdits ? localEdits[key] : overrides[activeLang]?.[key]
    if (!value) return

    setSaving(prev => ({ ...prev, [key]: true }))
    setSaveError(prev => ({ ...prev, [key]: null }))
    try {
      const res = await apiFetch(`/api/admin/ui-translations/${selectedTenant}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang: activeLang, key, value }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `HTTP ${res.status}`)
      }
      const data = await res.json()
      // Update overrides for all languages
      setOverrides(prev => {
        const updated = { ...prev }
        for (const [l, translatedValue] of Object.entries(data.translated)) {
          const defaultVal = defaults[l]?.[key]
          if (translatedValue === defaultVal) {
            if (updated[l]) {
              const { [key]: _, ...rest } = updated[l]
              updated[l] = rest
              if (Object.keys(updated[l]).length === 0) delete updated[l]
            }
          } else {
            updated[l] = { ...(updated[l] ?? {}), [key]: translatedValue }
          }
        }
        return updated
      })
      setLocalEdits(prev => { const n = { ...prev }; delete n[key]; return n })
    } catch (err) {
      setSaveError(prev => ({ ...prev, [key]: err.message || 'Fehler' }))
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }))
    }
  }

  const handleKeyDown = (e, key) => {
    if (e.key === 'Enter') e.target.blur()
  }

  const keys = getKeys()

  return (
    <div className="translations-page">
      <div className="translations-header">
        <div>
          <h1 className="page-title">UI-Texte</h1>
          <p className="page-subtitle">Texte im Kiosksystem anpassen</p>
        </div>

        {isOwner && tenants.length > 0 && (
          <select
            className="form-control translations-tenant-select"
            value={selectedTenant}
            onChange={e => setSelectedTenant(e.target.value)}
          >
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Language tabs */}
      <div className="translations-lang-tabs">
        {ALL_LANGUAGES.map(lang => (
          <button
            key={lang.code}
            className={`translations-lang-tab ${activeLang === lang.code ? 'active' : ''}`}
            onClick={() => setActiveLang(lang.code)}
          >
            <span>{lang.flag}</span>
            <span>{lang.name}</span>
            {overrides[lang.code] && Object.keys(overrides[lang.code]).length > 0 && (
              <span className="translations-lang-badge">
                {Object.keys(overrides[lang.code]).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="translations-toolbar">
        <input
          className="form-control translations-search"
          type="search"
          placeholder="Schlüssel oder Text suchen…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <label className="translations-filter-label">
          <input
            type="checkbox"
            checked={showOverrides}
            onChange={e => setShowOverrides(e.target.checked)}
          />
          Nur angepasste anzeigen
          {overrideCount > 0 && <span className="translations-override-count">{overrideCount}</span>}
        </label>
      </div>

      {loading ? (
        <div className="translations-empty">Laden…</div>
      ) : keys.length === 0 ? (
        <div className="translations-empty">
          {showOverrides ? 'Keine angepassten Texte für diese Sprache.' : 'Keine Einträge gefunden.'}
        </div>
      ) : (
        <div className="translations-table-wrap">
          <table className="translations-table">
            <thead>
              <tr>
                <th>Schlüssel</th>
                <th>Standardtext</th>
                <th>Ihr Text</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map(key => {
                const defaultVal = defaults[activeLang]?.[key] ?? ''
                const overridden = isOverridden(key)
                const currentVal = getValue(key)
                const isDirty = key in localEdits && localEdits[key] !== (overrides[activeLang]?.[key] ?? defaultVal)
                return (
                  <tr key={key} className={overridden ? 'row-overridden' : ''}>
                    <td className="translations-key">{key}</td>
                    <td className="translations-default">{defaultVal}</td>
                    <td className="translations-input-cell">
                      <input
                        className={`form-control translations-input ${isDirty ? 'dirty' : ''}`}
                        value={currentVal}
                        onChange={e => handleChange(key, e.target.value)}
                        onBlur={() => handleSave(key)}
                        onKeyDown={e => handleKeyDown(e, key)}
                        disabled={saving[key]}
                        placeholder={defaultVal}
                      />
                      {saveError[key] && <span className="translations-save-error">{saveError[key]}</span>}
                    </td>
                    <td className="translations-actions">
                      {saving[key] ? (
                        <span className="spinner spinner-sm" />
                      ) : (
                        <div className="translations-action-btns">
                          {isDirty && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleTranslate(key)}
                              title="In alle Sprachen übersetzen und speichern"
                            >
                              Speichern & Übersetzen
                            </button>
                          )}
                          {overridden && !isDirty && (
                            <button
                              className="btn btn-ghost btn-sm translations-reset-btn"
                              onClick={() => handleReset(key)}
                              title="Zurücksetzen auf Standard"
                            >
                              Zurücksetzen
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedTenant && (
        <VersionHistoryPanel table="ui_translations" tenantId={selectedTenant} onRestored={loadTranslations} />
      )}
    </div>
  )
}

export default Translations
